import {
  campaignDisplayLabel,
  centsFrom,
  formatMoney,
  isEmailAddress,
  surchargeFor,
} from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { boardIsLong, boardView } from '../lib/dedication-board'
import { siteGiftTo } from '../lib/public-router'
import { holdsThePayment, returnedCheckout } from '../lib/returned-checkout'
import { useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import {
  cardCostNote,
  continueLabel,
  giftThanks,
  type Thanks,
} from '../lib/two-step-payment'
import {
  Button,
  Card,
  Choice,
  Field,
  MoneyInput,
  Notice,
  PaymentFlow,
  PaymentStep,
  ReturnedCheckoutNotice,
  SearchField,
  SettingBlock,
  Toggle,
  TotalLine,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from '../ui'
import { Challenge } from '../ui/Challenge'
import { PublicNotFound } from '../ui/ErrorPage'
import { PublicShell } from '../ui/PublicShell'
import { CampaignAbout } from './CampaignAbout'
import { CampaignNotCollecting } from './CampaignNotCollecting'
import {
  answersFor,
  BesideGifts,
  CampaignQuestions,
  OWN,
  type PickedAnswers,
  questionsStanding,
} from './CampaignQuestions'
import { CampaignThanks } from './CampaignThanks'
import { DonatePage } from './DonatePage'
import { DonorFields } from './DonorFields'
import { GiveFrame } from './GiveFrame'
import {
  givingChoiceOffered,
  isKioskMode,
  useKioskActivity,
  useKioskBusy,
} from './kiosk'
import { PledgeForm } from './PledgeForm'
import { SplitIntoPlan } from './SplitIntoPlan'
import './campaign-board.css'
import './donate.css'
import './giving-cards.css'

const money = formatMoney

/**
 * One campaign year's donate page, at `/{orgId}/donate/{campaignId}`.
 *
 * Any address of a campaign — a year's id, the campaign's own, or one of its
 * names — opens the year collecting now, so last season's appeal and a link
 * printed once both reach this season. Whichever way in, the page shows one
 * year and a gift from it names that year. With no year collecting the page
 * says so and offers a general gift — a refusal with nowhere to go turns a
 * willing donor into a bounce; an address nothing answers to lands on the
 * general page.
 *
 * `?r=` carries who the appeal reached, signed by the server. The page sends it
 * back for verification before showing or filling identity, and through
 * checkout for attribution.
 */
export function CampaignDonatePage({
  orgId,
  campaignId,
  onLogin,
}: {
  orgId: string
  campaignId: string
  onLogin?: () => void
}) {
  const trpc = useTRPC()
  const params = new URLSearchParams(window.location.search)
  const kiosk = isKioskMode()
  const reportKioskActivity = useKioskActivity(kiosk)
  const kioskBusy = useKioskBusy(kiosk)
  const [completedCheckoutId, showOutcome] = useReturnedCheckout()
  /* What a paid gift is thanked for, taken from the checkout it was paid
     through at the moment it completed. A pledge, or a return after the page
     was reloaded, has none and is thanked without naming a gift. */
  const [thanks, setThanks] = useState<Thanks | null>(null)
  const today = useToday()
  const completePledge = (checkoutId: string) => {
    setThanks(null)
    showOutcome(checkoutId)
  }
  const confirmation = useCheckoutConfirmation({
    onComplete: (checkoutId) => {
      const v = checkout.variables
      setThanks(
        v && page.data
          ? giftThanks({
              amountCents: v.amountCents,
              coverFee: v.coverFee ?? false,
              forLabel:
                dedication?.label ??
                campaignDisplayLabel(page.data.label, page.data.period),
              email: v.donorEmail,
              on: today,
              beside: (v.companions ?? []).map((l) => {
                const x = page.data?.companions.find(
                  (o) => o.shortId === l.campaignShortId,
                )
                return {
                  label: x
                    ? campaignDisplayLabel(x.label, x.period)
                    : 'Another campaign',
                  amountCents: l.amountCents,
                }
              }),
            })
          : null,
      )
      showOutcome(checkoutId)
    },
  })
  const recipient = kiosk ? undefined : (params.get('r') ?? undefined)
  const page = useQuery({
    ...trpc.donation.campaign.queryOptions({
      shortId: orgId,
      campaignShortId: campaignId,
    }),
    /* An address nothing answers to will not start answering on a retry, and
       the donor waits on each one before reaching the page that can help. */
    retry: (failures, err) => err.data?.code !== 'NOT_FOUND' && failures < 3,
  })
  const outcome = useQuery({
    ...trpc.donation.checkoutOutcome.queryOptions({
      shortId: orgId,
      checkoutId: completedCheckoutId ?? '',
    }),
    enabled: completedCheckoutId !== null,
  })
  /* The figure as it is typed. A suggested amount FILLS this rather than
     standing beside it, so the page holds one answer to how much rather than
     two that can disagree about which was chosen last. */
  const [typed, setTyped] = useState('')
  /* What the donor adds to each campaign the page offers beside its own, as
     typed, by that campaign's address. Empty is no gift to it. */
  const [beside, setBeside] = useState<Record<string, string>>({})
  /* What the donor picked for each campaign's questions: this page's own
     under OWN, a companion's under its address. */
  const [picked, setPicked] = useState<Record<string, PickedAnswers>>({})
  const [coverFee, setCoverFee] = useState(false)
  const [unlisted, setUnlisted] = useState(false)
  const [saveCard, setSaveCard] = useState(!kiosk)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [recipientCleared, setRecipientCleared] = useState(false)
  /* The dedication picked off the board. Choosing one FILLS the figure and takes
     the amount question away: a dedication is a named thing at a published
     level, not a sum to name. */
  const [dedication, setDedication] = useState<{
    id: string
    label: string
    amountCents: number
  } | null>(null)
  /* Reserving one is a write that costs the sender nothing and takes a window
     off the board for half an hour, so the card cannot be the guard the way it
     is for a plain gift. The widget is drawn only where one is chosen. */
  /* The dedication somebody is PLEDGING rather than buying. It takes the gift
     half of the card while it is open, because a pledge is a different act and
     two halves of one card would read as one question. */
  const [pledging, setPledging] = useState<{
    id: string
    label: string
    amountCents: number
  } | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [spentAt, setSpentAt] = useState(0)
  const challenge = useQuery({
    ...trpc.turnstile.siteKey.queryOptions(),
    /* Wanted wherever the page could need it: a dedication, or a campaign
       here that asks its donors anything. */
    enabled:
      dedication !== null ||
      (page.data?.questions.length ?? 0) > 0 ||
      (page.data?.companions ?? []).some((x) => x.questions.length > 0),
  })
  const boundsId = useId()
  /* What the board is narrowed to. It hides rows and nothing else: a dedication
     already chosen stays chosen while it is filtered out of view, and the gift
     half goes on naming it. */
  const [boardQuery, setBoardQuery] = useState('')
  /* Whether a long board has been opened past its fold. */
  const [boardOpen, setBoardOpen] = useState(false)
  /* Opening the board unmounts the control that had focus, so focus moves to
     the first row it revealed that holds one — where the reader was headed. */
  const boardList = useRef<HTMLUListElement>(null)
  const revealFrom = useRef<string | null>(null)
  useEffect(() => {
    if (!boardOpen || revealFrom.current === null) return
    const rows = [...(boardList.current?.children ?? [])]
    const from = rows.findIndex(
      (row) => (row as HTMLElement).dataset.row === revealFrom.current,
    )
    revealFrom.current = null
    for (const row of from < 0 ? [] : rows.slice(from)) {
      const target = row.querySelector('button')
      if (target) return target.focus()
    }
  }, [boardOpen])
  const recipientIdentity = useQuery({
    ...trpc.donation.recipient.queryOptions({
      shortId: orgId,
      token: recipient ?? '',
    }),
    enabled: !kiosk && recipient !== undefined,
    retry: false,
  })
  /* The checkout this visitor went Back from. The next Continue names it, so
     the same answers reuse the dedication already held and changed ones give
     it back, rather than a second hold taking another window off the board. */
  const left = useRef<string | null>(null)
  const checkout = useMutation({
    ...trpc.donation.campaignCheckout.mutationOptions(),
    onSuccess: () => {
      left.current = null
    },
    /* A token is good once, whether the submit was taken or refused: a spent
       one left in place fails the next Continue, which Back makes likely.
       Offering the check again is the form's to ask. */
    onSettled: () => {
      setToken(null)
      setSpentAt(Date.now())
    },
  })

  /* The appeal's recipient fills the donor's fields once, when it is read.
     Filled again whenever the answers came back into view, it would put the
     recipient's details over whatever the donor had corrected them to. */
  const filledFor = useRef<string | null>(null)
  useEffect(() => {
    if (!recipientIdentity.data || recipientCleared) return
    const who = `${recipientIdentity.data.name}\n${recipientIdentity.data.email}`
    if (filledFor.current === who) return
    filledFor.current = who
    setName(recipientIdentity.data.name)
    setEmail(recipientIdentity.data.email)
  }, [recipientIdentity.data, recipientCleared])

  const returned = returnedCheckout({
    checkoutId: completedCheckoutId,
    status: outcome.data?.status,
    isError: outcome.isError,
  })
  const paid = returned === 'paid'
  useEffect(() => {
    if (paid || outcome.isError) kioskBusy.release()
  }, [kioskBusy, outcome.isError, paid])
  const startAgain = () => {
    showOutcome(null)
    setThanks(null)
    setTyped('')
    setBeside({})
    setPicked({})
    setCoverFee(false)
    setUnlisted(false)
    setName(kiosk ? '' : (recipientIdentity.data?.name ?? ''))
    setEmail(kiosk ? '' : (recipientIdentity.data?.email ?? ''))
    setRecipientCleared(false)
    setDedication(null)
    setPledging(null)
    setBoardQuery('')
    setBoardOpen(false)
    setToken(null)
    checkout.reset()
    confirmation.reset()
  }

  /* The org's own suggestions and the bounds the server takes a gift between.
     A drive is the org's, so its page suggests what the org suggests — one
     answer, rather than a second list to keep in step. */
  const presets = page.data?.suggestedAmountsCents ?? []
  const amountCents = centsFrom(typed)
  const valid =
    page.data !== undefined &&
    amountCents !== null &&
    amountCents >= page.data.minGiftCents &&
    amountCents <= page.data.maxGiftCents
  /* Each companion amount is a gift of its own, held to the same bounds as
     any gift; one typed out of bounds holds the page as the main figure does. */
  const besideTyped = (page.data?.companions ?? []).flatMap((x) => {
    const cents = centsFrom(beside[x.shortId] ?? '')
    return cents === null ? [] : [{ ...x, amountCents: cents }]
  })
  const inBounds = (cents: number) =>
    page.data !== undefined &&
    cents >= page.data.minGiftCents &&
    cents <= page.data.maxGiftCents
  const besideValid = besideTyped.every((l) => inBounds(l.amountCents))
  /* Only a figure the page would take is counted: a total stating a refused
     amount is a total the page will not charge. */
  const besideLines = besideTyped.filter((l) => inBounds(l.amountCents))
  const givenCents =
    (amountCents ?? 0) + besideLines.reduce((sum, l) => sum + l.amountCents, 0)
  const tooMuchAtOnce =
    page.data !== undefined && givenCents > page.data.maxGiftCents
  const feeCents =
    valid && amountCents !== null && coverFee ? surchargeFor(givenCents) : 0
  const chargedCents = givenCents + feeCents
  const checkoutGivenCents = checkout.variables
    ? checkout.variables.amountCents +
      (checkout.variables.companions ?? []).reduce(
        (sum, l) => sum + l.amountCents,
        0,
      )
    : null
  const checkoutTotalCents =
    checkout.variables && checkoutGivenCents !== null
      ? checkoutGivenCents +
        (checkout.variables.coverFee ? surchargeFor(checkoutGivenCents) : 0)
      : null
  const outOfBounds = amountCents !== null && !valid
  /* Above the ceiling is not the same answer as below the floor: a figure that
     size is a commitment rather than a typo, and the shul takes one as a pledge
     paid in installments. */
  const overCeiling =
    amountCents !== null &&
    page.data !== undefined &&
    amountCents > page.data.maxGiftCents
  const arranging = overCeiling && givingChoiceOffered('installment', kiosk)
  // A gift needs somebody's name on it to be thanked or reconciled, and an
  // address the payment itself requires — which is also what quietly
  // attributes the gift to a member.
  const named = name.trim().length > 0 && isEmailAddress(email)
  const { asks, unanswered } = questionsStanding(
    page.data?.questions ?? [],
    besideLines,
    picked,
  )
  /* A gift that keeps something of ours before it is paid — a dedication off
     the board, or a campaign's answers — carries the challenge; the server
     asks for it on the same terms. */
  const needsChallenge = dedication !== null || asks
  const challenged =
    !needsChallenge || !challenge.data?.siteKey || token !== null
  const ready =
    valid &&
    besideValid &&
    !tooMuchAtOnce &&
    named &&
    challenged &&
    unanswered === null

  if (page.error?.data?.code === 'NOT_FOUND') {
    return <DonatePage orgId={orgId} onLogin={onLogin} missedCampaign />
  }
  if (page.isLoading || page.error || !page.data) {
    return (
      <PublicShell
        name={null}
        orgId={null}
        current="donate"
        onLogin={onLogin}
        kiosk={kiosk}
      >
        {page.isLoading ? (
          <GiveFrame head={null} other={[]}>
            <Notice>Loading…</Notice>
          </GiveFrame>
        ) : (
          <PublicNotFound />
        )}
      </PublicShell>
    )
  }
  const c = page.data

  const longBoard = boardIsLong(c.dedications.length)
  const board = boardView(c.dedications, {
    query: boardQuery,
    open: boardOpen,
    keep: [dedication?.id, pledging?.id].filter((id) => id !== undefined),
  })
  const boardRows = board.peek ? [...board.shown, board.peek] : board.shown
  type BoardRow = (typeof boardRows)[number]

  /* What an entry says about how much of it is left, the same in both looks.
     One of a kind says nothing until it is gone; several say how many, which
     is what somebody deciding needs. Spoken for and not yet given is off the
     board either way, and a reader is owed the difference. */
  const boardCounts = (d: BoardRow): string[] => [
    ...(d.quantity !== null &&
    d.quantity > 1 &&
    !d.taken &&
    d.remaining !== null
      ? [`${d.remaining} of ${d.quantity} left`]
      : []),
    ...(d.pledged > 0 && !d.taken ? [`${d.pledged} pledged`] : []),
  ]

  /* Both looks draw the same act: a card on the board holds a real button
     rather than being one, so a taken entry is a muted statement in either. */
  const boardAction = (d: BoardRow) => {
    if (d.taken) {
      return (
        <span className="board__taken">
          {/* A pledge the office has still to answer is spoken for rather than
              given, and says so. */}
          {d.pledged > 0 && d.pledged >= (d.quantity ?? 1)
            ? 'Pledged'
            : d.quantity !== null && d.quantity > 1
              ? 'All dedicated'
              : 'Dedicated'}
        </span>
      )
    }
    /* One card payment is bounded however the board is priced, so a level
       past the ceiling is undertaken rather than bought — the same answer as
       one the shul never offered online. */
    if (
      d.payableOnline &&
      d.amountCents <= c.maxGiftCents &&
      c.collecting &&
      c.acceptsDonations
    ) {
      return (
        <Button
          variant="secondary"
          on={dedication?.id === d.id}
          onClick={() => {
            const next =
              dedication?.id === d.id
                ? null
                : {
                    id: d.id,
                    label: d.label,
                    amountCents: d.amountCents,
                  }
            setDedication(next)
            setTyped(next ? String(next.amountCents / 100) : '')
          }}
        >
          {dedication?.id === d.id ? 'Chosen' : 'Dedicate'}
        </Button>
      )
    }
    if (!givingChoiceOffered('pledge', kiosk)) return null
    /* A dedication at this level is not a card payment, so the page takes the
       undertaking instead — which is what the shul already does on paper. */
    return (
      <Button
        variant="secondary"
        on={pledging?.id === d.id}
        onClick={() =>
          setPledging(
            pledging?.id === d.id
              ? null
              : {
                  id: d.id,
                  label: d.label,
                  amountCents: d.amountCents,
                },
          )
        }
      >
        {pledging?.id === d.id ? 'Pledging' : 'Pledge'}
      </Button>
    )
  }

  return (
    <PublicShell
      name={c.orgName}
      nameHe={c.orgNameHe}
      language={c.publicLanguage}
      orgId={c.orgShortId}
      acceptsDonations={c.acceptsDonations}
      contact={c}
      current="donate"
      onLogin={onLogin}
      kiosk={kiosk}
    >
      {paid ? (
        <CampaignThanks
          thanks={thanks}
          unlisted={Boolean(checkout.variables?.unlisted)}
          orgShortId={c.orgShortId}
          orgName={c.orgName}
          kiosk={kiosk}
          onAnother={startAgain}
        />
      ) : (
        <GiveFrame
          head={
            <CampaignAbout
              label={c.label}
              period={c.period}
              tagline={c.tagline}
              appeal={c.appeal}
              goalCents={c.goalCents}
              pledgedCents={c.pledgedCents}
            />
          }
          other={kiosk ? [] : c.otherWaysToGive}
        >
          <ReturnedCheckoutNotice state={returned} />
          <PaymentFlow hidden={holdsThePayment(returned)}>
            {c.dedications.length > 0 && (
              <div className="board">
                {/* The whole board, taken entries included: what a shul has
                      already had given is part of what it is asking for. A
                      heading at the step of the gift half's, since the two are
                      the page's two questions. */}
                <h2 className="donate__lead">Dedication opportunities</h2>
                {/* A long board is found rather than read: the field narrows
                      it by label across every row, folded or not. */}
                {longBoard && (
                  <SearchField
                    value={boardQuery}
                    onChange={setBoardQuery}
                    placeholder="Search dedications"
                    ariaLabel="Search dedication opportunities"
                  />
                )}
                {longBoard && board.shown.length === 0 ? (
                  <p className="board__none" role="status">
                    No dedication matches “{boardQuery.trim()}”.
                  </p>
                ) : null}
                {/* Folded, the row past the fold peeks from under a fade and is
                      inert: it says there is more, and the control beneath it is
                      the way to it. */}
                <div
                  className={
                    c.boardLook === 'cards'
                      ? 'board__cards-frame'
                      : 'board__frame'
                  }
                  data-folded={board.folded ? '' : undefined}
                  hidden={board.shown.length === 0}
                >
                  <ul
                    className={
                      c.boardLook === 'cards' ? 'gcards' : 'board__list'
                    }
                    ref={boardList}
                  >
                    {boardRows.map((d) => (
                      <li
                        className={
                          c.boardLook === 'cards'
                            ? 'gcards__item'
                            : 'board__row'
                        }
                        key={d.id}
                        data-row={d.id}
                        data-peek={d === board.peek ? '' : undefined}
                        inert={d === board.peek}
                      >
                        {c.boardLook === 'cards' ? (
                          <article className="gcard">
                            {/* The opportunity's own words lead; whoever has
                                dedicated it follows beneath, and with nobody the
                                words stand alone. */}
                            <span className="board__card-label" dir="auto">
                              {d.label}
                            </span>
                            {d.dedicatedBy.length > 0 ? (
                              <span className="board__card-by" dir="auto">
                                {d.dedicatedBy.join(' · ')}
                              </span>
                            ) : null}
                            <span className="gcard__foot">
                              <span className="gcard__progress">
                                <span className="gcard__figures">
                                  {money(d.amountCents)}
                                </span>
                                {boardCounts(d).map((count) => (
                                  <span className="gcard__figures" key={count}>
                                    {count}
                                  </span>
                                ))}
                              </span>
                              {boardAction(d)}
                            </span>
                          </article>
                        ) : (
                          <>
                            <span className="board__name" dir="auto">
                              {d.label}
                            </span>
                            <span className="board__amount">
                              {money(d.amountCents)}
                            </span>
                            {d.dedicatedBy.length > 0 ? (
                              <span className="board__by" dir="auto">
                                {d.dedicatedBy.join(' · ')}
                              </span>
                            ) : null}
                            {boardCounts(d).map((count) => (
                              <span className="board__left" key={count}>
                                {count}
                              </span>
                            ))}
                            <span className="board__act">{boardAction(d)}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                {board.folded ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      revealFrom.current = board.peek?.id ?? null
                      setBoardOpen(true)
                    }}
                  >
                    Show all {c.dedications.length} dedications
                  </Button>
                ) : null}
              </div>
            )}

            {pledging && givingChoiceOffered('pledge', kiosk) ? (
              <PledgeForm
                orgId={orgId}
                campaignId={c.shortId}
                chargeItemId={pledging.id}
                amountCents={pledging.amountCents}
                forLabel={pledging.label}
                onCancel={() => setPledging(null)}
                onCheckoutComplete={completePledge}
              />
            ) : !c.collecting ? (
              <CampaignNotCollecting
                label={c.label}
                period={c.period}
                upcoming={c.upcoming}
                orgShortId={c.orgShortId}
                openShortId={c.openShortId}
                kiosk={kiosk}
              />
            ) : c.acceptsDonations ? (
              <>
                {/* Names the column the donor fills in, so the figures beside it
                      are read as the drive's and these as theirs. */}
                <h2 className="donate__lead">Your gift</h2>
                {recipientIdentity.data && !recipientCleared && (
                  <Card className="donate__identity">
                    <p>
                      Giving as <strong>{recipientIdentity.data.name}</strong> (
                      {recipientIdentity.data.email})
                    </p>
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setName('')
                        setEmail('')
                        setRecipientCleared(true)
                      }}
                    >
                      Not you? Clear details
                    </Button>
                  </Card>
                )}
                {dedication ? (
                  <div className="board__chosen">
                    <span className="board__chosen-name" dir="auto">
                      {dedication.label}
                    </span>
                    <span>{money(dedication.amountCents)}</span>
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setDedication(null)
                        setTyped('')
                      }}
                    >
                      Give something else instead
                    </Button>
                  </div>
                ) : null}
                {/* A shul that suggests nothing leaves the row out rather than
                      drawing an empty one: the typed figure is then the whole
                      question. */}
                {!dedication && presets.length > 0 && (
                  <Choice
                    label="How much would you like to give?"
                    appearance="chip"
                    value={
                      presets.some((p) => String(p / 100) === typed)
                        ? typed
                        : ''
                    }
                    options={presets.map((p) => ({
                      value: String(p / 100),
                      label: money(p),
                    }))}
                    onValueChange={setTyped}
                  />
                )}
                {/* A chosen dedication is a published level, so there is no
                      figure left to name and the control goes rather than sitting
                      inert beside an answer already given. */}
                {!dedication && (
                  <Field
                    label={presets.length > 0 ? 'Another amount' : 'Amount'}
                  >
                    <MoneyInput
                      value={typed}
                      min={c.minGiftCents / 100}
                      max={c.maxGiftCents / 100}
                      aria-invalid={outOfBounds || undefined}
                      aria-describedby={outOfBounds ? boundsId : undefined}
                      onChange={(e) => setTyped(e.target.value)}
                    />
                  </Field>
                )}
                {/* A figure outside the bounds says so where it was typed, bound
                      to the field: the commit can only go quiet, and a button that
                      does nothing and explains nothing reads as a broken page.
                      Above the ceiling is a different answer from below the floor —
                      a sum that size is arranged rather than refused. */}
                {arranging && amountCents !== null ? (
                  <SplitIntoPlan
                    orgId={c.orgShortId}
                    campaignId={c.shortId}
                    amountCents={amountCents}
                    forLabel={campaignDisplayLabel(c.label, c.period)}
                    donorName={name}
                    donorEmail={email}
                    describedId={boundsId}
                    onCheckoutComplete={completePledge}
                  />
                ) : outOfBounds ? (
                  <p className="donate__fine" id={boundsId} role="alert">
                    A gift is taken between {money(c.minGiftCents)} and{' '}
                    {money(c.maxGiftCents)}.
                  </p>
                ) : null}
                {/* Not beside a sum being arranged as a plan, which is a
                      pledge and asks nothing. */}
                {!arranging && (
                  <CampaignQuestions
                    questions={c.questions}
                    value={picked[OWN]}
                    onValueChange={(next) =>
                      setPicked((was) => ({ ...was, [OWN]: next }))
                    }
                  />
                )}
                {/* Not beside a sum being arranged as a plan: the plan asks
                      for the first campaign's figure alone, and a companion
                      field left showing would read as part of what is being
                      arranged. */}
                {!arranging && (
                  <BesideGifts
                    companions={c.companions}
                    minCents={c.minGiftCents}
                    maxCents={c.maxGiftCents}
                    typed={beside}
                    onTypedChange={(shortId, value) =>
                      setBeside((was) => ({ ...was, [shortId]: value }))
                    }
                    picked={picked}
                    onPickedChange={(shortId, next) =>
                      setPicked((was) => ({ ...was, [shortId]: next }))
                    }
                  />
                )}
                {tooMuchAtOnce && valid ? (
                  <p className="donate__fine" role="alert">
                    One payment carries at most {money(c.maxGiftCents)}. Give
                    the rest separately.
                  </p>
                ) : null}
                <div className="donate__donor">
                  <DonorFields
                    name={name}
                    onName={setName}
                    email={email}
                    onEmail={setEmail}
                    kiosk={kiosk}
                  />
                </div>
                {/* A setting, not a field: it changes what the charge IS. The
                      sentence states what it WOULD add, because a control offering
                      "$0" gives nothing to decide. */}
                {!kiosk ? (
                  <SettingBlock
                    title="Cover the card processing cost"
                    description={
                      valid && amountCents !== null
                        ? `Adds ${money(surchargeFor(givenCents))}, so the whole ${money(givenCents)} reaches ${siteGiftTo(c.orgName)}.`
                        : `Adds the card processing cost, so the whole gift reaches ${siteGiftTo(c.orgName)}.`
                    }
                  >
                    <Toggle
                      checked={coverFee}
                      onChange={setCoverFee}
                      label="Cover the card processing cost"
                      hideLabel
                    />
                  </SettingBlock>
                ) : null}
                {/* A setting like the one above it, and the same shape: it
                      changes what the shul may do with the gift rather than what
                      the gift costs. Off by default — a donor who says nothing has
                      asked for nothing. */}
                <SettingBlock
                  title="Do not list my name"
                  info="We will still have your name for the receipt. It is left out where donors are named."
                >
                  <Toggle
                    checked={unlisted}
                    onChange={setUnlisted}
                    label="Do not list my name"
                    hideLabel
                  />
                </SettingBlock>
                {/* On by default, and the one setting here that is: the card
                      frame discloses that the shul may charge this card again,
                      and a disclosure nobody can decline is not one. */}
                {!kiosk ? (
                  <SettingBlock
                    title="Keep my card for next time"
                    info="We can use it for what you pay next. It is never charged without you setting that up."
                  >
                    <Toggle
                      checked={saveCard}
                      onChange={setSaveCard}
                      label="Keep my card for next time"
                      hideLabel
                    />
                  </SettingBlock>
                ) : null}
                {valid && amountCents !== null && (
                  <div className="donate__total">
                    <TotalLine
                      label={
                        dedication
                          ? dedication.label
                          : campaignDisplayLabel(c.label, c.period)
                      }
                      amount={money(amountCents)}
                    />
                    {besideLines.map((l) => (
                      <TotalLine
                        key={l.shortId}
                        label={campaignDisplayLabel(l.label, l.period)}
                        amount={money(l.amountCents)}
                      />
                    ))}
                    {coverFee && (
                      <TotalLine
                        label="Card processing"
                        amount={money(feeCents)}
                      />
                    )}
                    <TotalLine
                      label="Total"
                      amount={money(chargedCents)}
                      emphasis="total"
                    />
                  </div>
                )}
                {needsChallenge && challenge.data?.siteKey && (
                  <Challenge
                    siteKey={challenge.data.siteKey}
                    onToken={setToken}
                    spentAt={spentAt}
                  />
                )}
                {checkout.error && (
                  <Notice tone="error">{checkout.error.message}</Notice>
                )}
                <Button
                  onClick={() => {
                    if (ready && amountCents !== null)
                      checkout.mutate({
                        shortId: orgId,
                        campaignShortId: c.shortId,
                        amountCents,
                        coverFee,
                        unlisted,
                        saveCard: kiosk ? false : saveCard,
                        recipient,
                        donorName: name.trim(),
                        donorEmail: email.trim() || undefined,
                        chargeItemId: dedication?.id,
                        challenge: token,
                        replaces: left.current ?? undefined,
                        companions: besideLines.map((l) => ({
                          campaignShortId: l.shortId,
                          amountCents: l.amountCents,
                          answers: answersFor(l.questions, picked[l.shortId]),
                        })),
                        answers: answersFor(c.questions, picked[OWN]),
                        kiosk: kiosk || undefined,
                      })
                  }}
                  disabled={!ready || checkout.isPending}
                >
                  {continueLabel(checkout.isPending)}
                </Button>
                <p className="donate__fine" aria-live="polite">
                  {valid && unanswered
                    ? unanswered
                    : 'Next, your card. Nothing is charged yet.'}
                </p>
                {checkout.data && checkout.variables && (
                  <PaymentStep
                    orgId={orgId}
                    card={checkout.data.card}
                    what={[
                      dedication?.label ??
                        campaignDisplayLabel(c.label, c.period),
                      ...(checkout.variables.companions ?? []).map((l) => {
                        const x = c.companions.find(
                          (o) => o.shortId === l.campaignShortId,
                        )
                        return x
                          ? campaignDisplayLabel(x.label, x.period)
                          : 'another campaign'
                      }),
                    ].join(' and ')}
                    who={`Giving as ${[
                      checkout.variables.donorName,
                      checkout.variables.donorEmail,
                    ]
                      .filter(Boolean)
                      .join(', ')}`}
                    amount={money(checkoutTotalCents ?? 0)}
                    amountNote={cardCostNote(
                      (checkoutTotalCents ?? 0) - (checkoutGivenCents ?? 0),
                    )}
                    payLabel={`Pay ${money(checkoutTotalCents ?? 0)}`}
                    confirmation={confirmation}
                    onCardChange={reportKioskActivity}
                    onConfirmingChange={kioskBusy.setBusy}
                    onBack={() => {
                      left.current = checkout.data?.checkoutId ?? null
                      checkout.reset()
                      confirmation.reset()
                    }}
                  />
                )}
              </>
            ) : (
              <Notice tone="info">
                We are not set up to accept online donations yet.
              </Notice>
            )}
          </PaymentFlow>
        </GiveFrame>
      )}
    </PublicShell>
  )
}
